import React from 'react';
import LegalLayout from './LegalLayout';
import useSiteSettings from '../hooks/useSiteSettings';

const TermsOfServicePage = () => {
  const s = useSiteSettings();
  return (
  <LegalLayout title="Terms of Service" lastUpdated="February 03, 2026" testId="terms-page">
    <p>
      Welcome to <strong>BeeWeddy</strong>. By creating an account or using the Service you agree to these Terms of Service ("Terms").
      Please read carefully — these Terms include important information about platform fees, refunds, prohibited content,
      dispute resolution, and limitation of liability.
    </p>

    <h2>1. Who We Are & What We Do</h2>
    <p>
      BeeWeddy is a <strong>marketplace platform</strong> connecting customers planning events (weddings, birthdays, etc.) with
      independent vendors and professionals (caterers, photographers, decorators, venues, performers, etc.). We are an
      <strong> intermediary</strong> as defined under Section 2(1)(w) of the Information Technology Act, 2000 — we do <em>not</em> ourselves
      provide event services. Bookings are contracts between the customer and the listed vendor; BeeWeddy facilitates discovery,
      pricing, and payment routing.
    </p>

    <h2>2. Eligibility</h2>
    <ul>
      <li>You must be <strong>at least 18 years old</strong> and capable of forming a legally binding contract under the Indian Contract Act, 1872.</li>
      <li>If you register on behalf of a business, you confirm you are authorized to bind that business.</li>
      <li>You must provide accurate registration details and keep them updated.</li>
    </ul>

    <h2>3. Accounts & Roles</h2>
    <p>The Service supports five roles, each with specific responsibilities:</p>
    <ul>
      <li><strong>Customer:</strong> browses vendors, builds Instant Pricing packages, submits booking requests.</li>
      <li><strong>Vendor:</strong> lists services and packages, sets pricing, accepts/rejects bookings, must deliver as promised.</li>
      <li><strong>Professional</strong> (chef, photographer, performer, etc.): solo provider with profile + portfolio.</li>
      <li><strong>Captain:</strong> on-ground event coordinator assigned by admin; manages attendance.</li>
      <li><strong>Admin:</strong> BeeWeddy internal staff with platform-management privileges.</li>
    </ul>

    <h2>4. Vendor Listings & User-Generated Content (UGC)</h2>
    <p>By posting listings, photos, descriptions, custom themes or reviews ("Content") you grant BeeWeddy a worldwide,
      royalty-free, non-exclusive license to host, display and distribute that Content within the Service.</p>

    <p><strong>You must not post Content that:</strong></p>
    <ul>
      <li>Is unlawful, defamatory, obscene, sexually explicit, hateful, harassing or threatens any person or community;</li>
      <li>Infringes intellectual property, privacy, or publicity rights of any third party;</li>
      <li>Contains malware, phishing links, or spam;</li>
      <li>Misrepresents your services, prices, or qualifications (no fake portfolios);</li>
      <li>Targets minors or depicts them in inappropriate contexts;</li>
      <li>Violates Indian law including the IT Rules, 2021.</li>
    </ul>

    <p>
      We use a combination of automated and manual moderation. We may remove Content, suspend or ban accounts, and report
      illegal Content to authorities. Other users can flag Content via the in-app <em>Report</em> button.
    </p>

    <h2>5. Bookings, Pricing & Platform Commission</h2>
    <ul>
      <li>Vendors set base prices; BeeWeddy adds an <strong>admin-controlled platform commission</strong> (currently 8% on professionals, 10% on vendors, 12% on venues — visible to customers as the final price; vendors receive their listed amount).</li>
      <li>Customers see the marked-up final number with a single "*incl. platform fees" footnote. The breakdown is internal.</li>
      <li>Bookings via the Submit Booking flow are <strong>requests</strong> until the vendor / admin confirms.</li>
      <li>Once confirmed, both parties are bound to attend and deliver the service on the agreed date.</li>
    </ul>

    <h2>6. Refund & Cancellation</h2>
    <p>
      Refund timelines, cancellation windows and dispute resolution are governed by our separate{' '}
      <a href="/refund-policy">Refund Policy</a>. By using the Service you accept that policy as part of these Terms.
    </p>

    <h2>7. Wallet, Invite & Earn, Withdrawals</h2>
    <ul>
      <li>BeeWeddy may credit promotional wallet bonuses (e.g., ₹50 invite bonus). Wallet credits are <strong>promotional incentives</strong>, not stored value or e-money under the RBI's PPI Master Directions.</li>
      <li>Bonuses may be <strong>clawed back</strong> if the invited user does not transact within 90 days, or in case of fraud / multiple accounts / fake referrals.</li>
      <li>Withdrawals are subject to a minimum invite count (configurable) and admin approval. Approved withdrawals are settled to your registered bank/UPI within 7 working days.</li>
      <li>Misuse, self-referrals, or chargebacks may forfeit your wallet balance.</li>
    </ul>

    <h2>8. Captain & Attendance Verification</h2>
    <p>
      For confirmed package bookings, BeeWeddy may auto-assign a Captain who sets the venue location and verifies professional
      attendance via geofenced check-in. By accepting an event assignment as a professional, you consent to one-time GPS use at
      the moment you tap "Check In". Refusal to check-in may impact your rating and future assignments.
    </p>

    <h2>9. Prohibited Activities</h2>
    <ul>
      <li>Scraping, reverse-engineering, or interfering with the Service;</li>
      <li>Bypassing the platform to transact off-platform with vendors discovered via BeeWeddy (we may charge a 25% facilitation fee on such circumvented bookings);</li>
      <li>Creating multiple accounts to abuse referral bonuses;</li>
      <li>Impersonating others or providing false KYC.</li>
    </ul>

    <h2>10. Termination</h2>
    <ul>
      <li>You may terminate by deleting your account at any time (<a href="/delete-my-account">/delete-my-account</a>).</li>
      <li>We may suspend or terminate accounts for breach of these Terms, with or without notice depending on severity.</li>
      <li>Pending bookings at termination are governed by the Refund Policy.</li>
    </ul>

    <h2>11. Disclaimers & Limitation of Liability</h2>
    <p>
      BeeWeddy is provided "as is". We do not guarantee specific outcomes, vendor performance, or that listings are error-free.
      To the maximum extent permitted by law, our total liability for any claim is limited to the platform commission we
      actually earned on the disputed booking, and we are not liable for indirect, incidental, or consequential damages.
    </p>

    <h2>12. Governing Law & Dispute Resolution</h2>
    <p>
      These Terms are governed by the laws of India. Disputes will first be attempted to be resolved through our{' '}
      <a href="/privacy-policy">Grievance Officer</a>. Unresolved disputes are subject to the exclusive jurisdiction of courts at
      <strong> {s.jurisdiction_city}, Tamil Nadu</strong>. Where applicable, claims may be referred to mediation under the Mediation Act, 2023.
    </p>

    <h2>13. Changes</h2>
    <p>
      We may revise these Terms. Material changes will be notified in-app 7 days before the effective date. Continued use after
      that date means you accept the revised Terms.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions? Email <a href={`mailto:${s.legal_email}`} data-testid="terms-legal-email">{s.legal_email}</a>.
    </p>
  </LegalLayout>
  );
};

export default TermsOfServicePage;
